import React, { useState } from "react";
import { useQuery } from "react-query";
import { useAuth } from "../contexts/AuthContext";
import { queryAPI } from "../services/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Save, Info, AlertCircle, CheckCircle2, Database } from "lucide-react";
import { useNavigate } from "react-router-dom";

const SettingsPage: React.FC = () => {
  const { subdomain, setSubdomain } = useAuth();
  const [newSubdomain, setNewSubdomain] = useState(subdomain || "");
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const navigate = useNavigate();

  const { data: schema } = useQuery("schema", queryAPI.getSchema, {
    enabled: !!subdomain,
    retry: false,
  });

  const handleSaveSubdomain = () => {
    if (newSubdomain.trim()) {
      setSubdomain(newSubdomain);
      setSaveMessage({
        type: "success",
        text: "Subdomain saved successfully!",
      });
    }
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Settings</h1>

      {saveMessage && (
        <Alert
          className="mb-6"
          variant={saveMessage.type === "error" ? "destructive" : "default"}
        >
          {saveMessage.type === "success" ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{saveMessage.text}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Tenant Configuration</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="subdomain"
                  className="block text-sm font-medium mb-2"
                >
                  Subdomain
                </label>
                <Input
                  id="subdomain"
                  type="text"
                  value={newSubdomain}
                  onChange={(e) => setNewSubdomain(e.target.value)}
                  placeholder="Enter your tenant subdomain"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your tenant subdomain (e.g., 'acme' for
                  acme.example.com)
                </p>
              </div>
              <Button
                onClick={handleSaveSubdomain}
                disabled={!newSubdomain.trim() || newSubdomain === subdomain}
              >
                <Save className="mr-2 h-4 w-4" />
                Save Subdomain
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Connection Status</CardTitle>
          </CardHeader>
          <CardContent>
            {subdomain ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Current Subdomain
                  </p>
                  <p className="text-2xl font-semibold">{subdomain}</p>
                </div>
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-green-600 font-medium">Connected</p>
                </div>
              </div>
            ) : (
              <p className="text-muted-foreground">
                Configure your subdomain to connect to the system
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>Database Schema</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center text-muted-foreground">
                <Info className="mr-2 h-4 w-4" />
                <span className="text-sm">Manage your database schema</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/schema")}
              >
                <Database className="mr-2 h-4 w-4" />
                Open Schema Editor
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {schema ? (
            <>
              <div className="grid gap-4 md:grid-cols-3 mb-6">
                <div>
                  <p className="text-sm text-muted-foreground">Version</p>
                  <p className="text-lg font-medium">{schema.version}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tables</p>
                  <p className="text-lg font-medium">{schema.tables.length}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Schema Type</p>
                  <p className="text-lg font-medium">E-Commerce Database</p>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Tables</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {schema.tables.map((table) => (
                    <Card key={table.name} className="border">
                      <CardHeader>
                        <CardTitle className="text-base">
                          {table.name}
                        </CardTitle>
                        <CardDescription>{table.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {table.columns.length} columns
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          ) : subdomain ? (
            <p className="text-muted-foreground">
              Loading schema information...
            </p>
          ) : (
            <p className="text-muted-foreground">
              Configure your subdomain to view the database schema.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
